import * as focusTrap from 'focus-trap';
import { lock, unlock } from 'tua-body-scroll-lock';
import cookieHandler from '../utils/_cookieHandler';
import { keepScrollbarY } from '../utils/_scrollUtils';

const customSelect = require('../utils/_customSelect');

class oSidebarAjax extends HTMLDivElement {
    constructor() {
        super();
        this.overlayClose = this.querySelector('[is="a-overlay"]');
        this.slidePanel = this.querySelector('.slide-panel');
        this.slideFooter = this.querySelector('.footer');
        this.navigation = document.querySelector('[is="o-navigation"]');
        this.updateBodyScrollElements();

        // bind this
        this.setSidebarTrigger = this.setSidebarTrigger.bind(this);
        this.onCloseSlidebar = this.onCloseSlidebar.bind(this);
        this.actionKeyboard = this.actionKeyboard.bind(this);
        this.callRemote = this.callRemote.bind(this);
        this.afterContentLoadedEvents = this.afterContentLoadedEvents.bind(this);
        this.handleUrlParams = this.handleUrlParams.bind(this);
        this.waitForOneTrustCookiesPopinClose = this.waitForOneTrustCookiesPopinClose.bind(this);
        this.prepareAndSendEvent = this.prepareAndSendEvent.bind(this);
        this.eventSorter = this.eventSorter.bind(this);
        this.phonePrefixDeactivateTrap = this.phonePrefixDeactivateTrap.bind(this);
        this.phonePrefixActivateTrap = this.phonePrefixActivateTrap.bind(this);
    }

    connectedCallback() {
        this.handleUrlParams();
        document.addEventListener('click', this.setSidebarTrigger);
        document.addEventListener('oSidebarAjax:prepareAndSendEvent', this.eventSorter);
        document.addEventListener('oSidebarAjax:onCloseSlidebar', this.onCloseSlidebar);
        this.overlayClose.addEventListener('click', this.onCloseSlidebar);

        this.buttonClose = this.querySelectorAll('.close');
        if (this.buttonClose) {
            this.buttonClose.forEach((el) => {
                el.addEventListener('click', this.onCloseSlidebar);
            });
        }

        // Fix native scrollbar toggling loop on Chrome
        this.scrollObserver = keepScrollbarY(this.slidePanel);
    }

    /**
     * Update list of elements that you don't want the touch/scroll gesture to
     * go through and scroll the body page behind (only for iOS).
     * Used by the `tua-body-scroll-lock` library.
     * Note: `overscroll-behavior: none` is not a working alternative (Tested on iOS 17.2).
     */
    updateBodyScrollElements() {
        this.bodyScrollElements = [
            this.slidePanel,
            ...this.querySelectorAll('.choices__list'),
        ];
    }

    eventSorter(e) {
        if (this.id === e.detail.sidebarId) {
            this.prepareAndSendEvent(this.dataset);
        }
    }

    handleUrlParams() {
        const querystring = new URLSearchParams(window.location.search);
        const paramLayer = querystring.get('layer');

        if (paramLayer && this.id === `o-sidebar-${paramLayer}`) {
            const hasClosedOneTrustCookiesPopin = cookieHandler.read('OptanonAlertBoxClosed');

            if (!hasClosedOneTrustCookiesPopin) {
                this.waitInterval = setInterval(() => {
                    this.waitForOneTrustCookiesPopinClose(this.dataset.urltarget);
                }, 500);
            } else {
                this.sendEvent(this.dataset.urltarget);
            }
        }
    }

    waitForOneTrustCookiesPopinClose(urltarget) {
        if (cookieHandler.read('OptanonAlertBoxClosed')) {
            this.sendEvent(urltarget);
            clearInterval(this.waitInterval);
        }
    }

    disconnectedCallback() {
        document.removeEventListener('click', this.setSidebarTrigger);
        if (this.buttonOpen) this.buttonOpen.removeEventListener('click', this.onOpenSlidebar);
        if (this.buttonClose) this.buttonClose.removeEventListener('click', this.onCloseSlidebar);
        if (this.overlayClose) this.overlayClose.removeEventListener('click', this.onCloseSlidebar);
        this.trap.deactivate(); // just in case
    }

    setSidebarTrigger(e) {
        const btnTarget = e.target.closest('[data-target]');

        if (btnTarget && btnTarget.hasAttribute('data-target') && btnTarget.dataset.target === `#${this.id}`) {
            if (btnTarget.dataset.setActive !== undefined) {
                this.activationButton = btnTarget; // button to toggle .active class
            }
            const data = {};
            data.dataset = btnTarget.dataset ? btnTarget.dataset : '';
            data.target = (data.dataset && data.dataset.target) ? data.dataset.target : `#${this.id}`;
            this.prepareAndSendEvent(data.dataset);
        }
    }

    phonePrefixDeactivateTrap() {
        this.trap.deactivate();
        unlock(this.bodyScrollElements);
    }

    phonePrefixActivateTrap() {
        this.trap.activate();
        lock(this.bodyScrollElements);
    }

    onOpenSlidebar() {
        const isClosed = !this.getAttribute('aria-expanded') || this.getAttribute('aria-expanded') === 'false';
        if (isClosed) {
            if (this.id === 'o-sidebar-search') {
                this.querySelector('section.footer').setAttribute('hidden', true);
            }
            this.slidePanel.setAttribute('aria-hidden', false);
            this.setAttribute('tabindex', 0);
            this.setAttribute('aria-expanded', true);

            if (document.querySelectorAll('[is="o-sidebar"][aria-expanded="true"], [is="o-sidebar-ajax"][aria-expanded="true"]').length > 1) { // check if another sidebar is already open
                this.classList.add('another-sidebar-already-active');
            }

            if (this.slideFooter !== null) {
                const footerHeight = this.slideFooter.clientHeight;
                this.style.setProperty('--slide-footer-height', `${footerHeight}px`);
            } else {
                this.style.setProperty('--slide-footer-height', '0px');
            }
            if (this.activationButton) {
                this.activationButton.classList.add('active');
            }
            this.overlayClose.classList.add('css-show');

            window.addEventListener('keydown', this.actionKeyboard);

            // if phone prefix is open desactive focus trap + unlock scroll
            document.body.addEventListener('open:countrydropdown', this.phonePrefixDeactivateTrap);

            // if phone prefix is closed reactivate focus trap + lock scroll
            document.body.addEventListener('close:countrydropdown', this.phonePrefixActivateTrap);
        } else {
            unlock(this.bodyScrollElements);
            this.trap.deactivate();
        }
        document.dispatchEvent(new CustomEvent('oSidebar:open', { detail: { sidebarId: this.id, sidebar: this } }));
    }

    onCloseSlidebar() {
        unlock(this.bodyScrollElements);
        if (this.navigation) {
            // Tell o-navigation to stop listening to scroll events
            // otherwise it will close the menu on mobile
            this.navigation.waitBeforeScrollEvent = true;
            setTimeout(() => { // tell o-navigation to restart listening…
                this.navigation.waitBeforeScrollEvent = false;
            }, 200);
        }

        setTimeout(() => {
            this.classList.remove('another-sidebar-already-active'); // remove the class after closing animation if it's here
        }, 200);
        if (this.trap) this.trap.deactivate();
        this.overlayClose.classList.remove('css-show');
        this.slidePanel.setAttribute('aria-hidden', true);
        this.setAttribute('tabindex', -1);
        this.setAttribute('aria-expanded', false);

        if (this.activationButton) {
            this.activationButton.classList.remove('active');
        }

        if (this.contentButtonClose) {
            this.contentButtonClose.forEach((el) => {
                el.removeEventListener('click', this.onCloseSlidebar);
            });
        }
        document.dispatchEvent(new CustomEvent('oSidebar:onCloseSlidebar', { detail: { sidebarId: this.id, sidebar: this } }));
        window.removeEventListener('keydown', this.actionKeyboard);

        document.body.removeEventListener('close:countrydropdown', this.phonePrefixActivateTrap);
        document.body.removeEventListener('open:countrydropdown', this.phonePrefixActivateTrap);
        this.cleanCustomSelect();
    }

    actionKeyboard(event) {
        if (event.code === 'Escape') {
            if (document.querySelector('[is="m-popin"][aria-hidden="false"]')) {
                return;
            }
            if (document.querySelectorAll('[is="o-sidebar"][aria-expanded="true"], [is="o-sidebar-ajax"][aria-expanded="true"]').length > 1) { // in case of multiple sidebar open
                if (this.classList.contains('another-sidebar-already-active')) { // only close sidebar if it's the 2nd one
                    this.onCloseSlidebar();
                }
            } else {
                this.onCloseSlidebar();
            }
        }
    }

    prepareAndSendEvent(btnDataset) {
        let url = this.dataset.urltarget;
        let loadBundleAjax;

        // Add necessary querystring depending on layer
        switch (this.id) {
            case 'o-sidebar-miniCart':
                loadBundleAjax = document.querySelector('#loadBundleAjax');

                // if open mini cart : add bundle script for Paypal Express
                if (loadBundleAjax) {
                    const script = document.createElement('script');
                    script.src = loadBundleAjax.value;
                    document.body.appendChild(script);
                }

                break;
            case 'o-sidebar-backinstock':
            case 'o-sidebar-findInStore':
                if (this.id === 'o-sidebar-findInStore') {
                    const currentPid = document.querySelector('.product-detail').getAttribute('data-pid');
                    url = `${url}?pid=${currentPid}`;
                }
                if (btnDataset.pid) {
                    url = `${url}?pid=${btnDataset.pid}`;
                }
                if (btnDataset.sizeid) {
                    url = `${url}&size=${btnDataset.sizeid}`;
                }
                break;
            case 'o-sidebar-updateAddress':
            case 'o-sidebar-addAddress':
                if (btnDataset.href) {
                    url = btnDataset.href;
                } else if (btnDataset.addressid) {
                    url = `${url}?addressId=${btnDataset.addressid}`;
                }
                if (btnDataset.mode === 'edit') {
                    this.querySelector('.address-update').classList.remove('d-none');
                    this.querySelector('.address-add').classList.add('d-none');
                } else {
                    this.querySelector('.address-add').classList.remove('d-none');
                    this.querySelector('.address-update').classList.add('d-none');
                }
                break;
            default:
        }
        this.sendEvent(url);
    }

    sendEvent(url) {
        const newUrl = this.handleQueryString(url);
        this.onOpenSlidebar();

        if (newUrl) {
            const htmlTarget = this.querySelector('.content');
            this.callRemote(newUrl, 'GET', htmlTarget);
        } else {
            // no url included but try to init some way out
            this.afterContentLoadedEvents();
        }
    }

    // eslint-disable-next-line class-methods-use-this
    handleQueryString(url) {
        const querystring = new URLSearchParams(window.location.search);
        const pid = querystring.get('pid');
        const size = querystring.get('size');

        let newUrl = url;
        if (pid) {
            newUrl += `?pid=${pid}`;
            if (size) {
                newUrl += `&size=${size}`;
            }
        }
        return newUrl;
    }

    callRemote(url, method, htmlElement) {
        const htmlNode = htmlElement;
        this.classList.add('loading');

        fetch(url, { Method: method })
            .then((response) => response.text())
            .then((htmlData) => {
                htmlNode.innerHTML = htmlData;
                this.afterContentLoadedEvents();
                this.classList.remove('loading');
                const gtmData = htmlNode.querySelector('input[data-gtm-information]');
                if (gtmData) {
                    document.dispatchEvent(new CustomEvent('gtm:pushEvent', {
                        detail: {
                            data: JSON.parse(gtmData.dataset.gtmInformation),
                            pageType: true,
                        },
                    }));
                }
            });
    }

    afterContentLoadedEvents() {
        const content = this.querySelector('.content');
        const closeButton = this.querySelector('.close');
        if (content) {
            this.contentButtonClose = content.querySelectorAll('.close');
            if (this.contentButtonClose) {
                this.contentButtonClose.forEach((el) => {
                    el.addEventListener('click', this.onCloseSlidebar);
                });
            }
        }
        this.updateBodyScrollElements();
        lock(this.bodyScrollElements);
        // Custom selects can have scrollbar so we will need to update the lock
        // once they are loaded
        setTimeout(() => {
            // We wait until the Choices.js element is initialized
            // There is probably a more reliable way to do this
            if (this.slidePanel.getAttribute('aria-hidden') === 'true') {
                return;
            }
            unlock(this.bodyScrollElements);
            this.updateBodyScrollElements();
            lock(this.bodyScrollElements);
        }, 200);
        this.trap = focusTrap.createFocusTrap(this, {
            initialFocus: closeButton || undefined,
            allowOutsideClick: true,
        });
        this.trap.activate();
        customSelect.initCustomSelect(this);
    }

    cleanCustomSelect() {
        const customSelects = this.querySelectorAll('.custom-select');
        customSelects.forEach((select) => {
            customSelect.destroyInstanceFromId(select.id);
        });
    }
}

customElements.define('o-sidebar-ajax', oSidebarAjax, {
    extends: 'div',
});
